import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Share2, X } from "lucide-react";
import { toast } from "sonner";
import { getGlobalStats } from "../lib/stats";

const EMOJI = { correct: "🟩", present: "🟨", absent: "⬛" };

export default function StatsModal({ open, onClose, lastResult, puzzleTitle }) {
  const stats = getGlobalStats();
  const winPct = stats.played > 0 ? Math.round((stats.wins / stats.played) * 100) : 0;
  const maxDist = Math.max(1, ...Object.values(stats.distribution));

  const share = () => {
    if (!lastResult) return;
    const grid = lastResult.guesses
      .map((g) => g.results.map((r) => EMOJI[r]).join(""))
      .join("\n");
    const header = `Brush Up · ${puzzleTitle || ""}\n${lastResult.won ? lastResult.guessesUsed : "X"}/${lastResult.maxGuesses}`;
    const text = `${header}\n\n${grid}\n\n— The Nevada Sagebrush`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      toast.success("Results copied to clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md p-0 border-0 rounded-none bg-transparent shadow-none"
        data-testid="stats-modal"
      >
        <div className="rule-thick" />
        <div className="bg-white border-l-2 border-r-2 border-b-2 border-[color:var(--ink-navy)] p-6">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0">
            <DialogTitle className="font-serif-display text-3xl">Reader Stats</DialogTitle>
            <button onClick={onClose} data-testid="stats-close">
              <X size={20} />
            </button>
          </DialogHeader>

          {lastResult && (
            <div className="mt-4 p-3 border border-[color:var(--ink-navy)] bg-[color:var(--paper)]" data-testid="last-result">
              <p className="font-serif-display text-xl">
                {lastResult.won ? "Extra! Extra!" : "Better luck next issue."}
              </p>
              <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                {lastResult.won
                  ? `Solved in ${lastResult.guessesUsed} guess${lastResult.guessesUsed > 1 ? "es" : ""}.`
                  : `The word was ${lastResult.answer}.`}
              </p>
            </div>
          )}

          <div className="grid grid-cols-4 gap-0 mt-6 border border-[color:var(--ink-navy)]">
            {[
              ["Played", stats.played],
              ["Win %", `${winPct}`],
              ["Streak", stats.currentStreak],
              ["Best", stats.maxStreak],
            ].map(([label, val], i) => (
              <div
                key={label}
                className={`py-3 text-center ${i > 0 ? "border-l border-[color:var(--ink-navy)]" : ""}`}
                data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, '')}`}
              >
                <div className="font-serif-display text-2xl">{val}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono-plex" style={{ color: "var(--muted)" }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest font-mono-plex mb-2" style={{ color: "var(--muted)" }}>
              Guess Distribution
            </div>
            <div className="flex flex-col gap-1">
              {Object.entries(stats.distribution)
                .filter(([k]) => Number(k) <= 8)
                .map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <div className="w-4 font-mono-plex text-sm">{k}</div>
                    <div className="flex-1 h-5 bg-[color:var(--paper-2)]">
                      <div
                        className="h-full bg-[color:var(--ink-navy)] text-white text-xs px-2 flex items-center justify-end font-mono-plex"
                        style={{ width: `${(v / maxDist) * 100}%`, minWidth: v > 0 ? "1.5rem" : "0" }}
                      >
                        {v > 0 ? v : ""}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {lastResult && (
            <button
              className="btn-editorial w-full mt-6 flex items-center justify-center gap-2"
              onClick={share}
              data-testid="share-button"
            >
              <Share2 size={16} /> Share Results
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
